import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { nl } from '../locales/nl';
import { fr } from '../locales/fr';
import { translationAPI } from '../services/api';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const languages = {
  tr: { flag: '🇹🇷', name: 'Türkçe', translations: tr },
  en: { flag: '🇬🇧', name: 'English', translations: en },
  nl: { flag: '🇳🇱', name: 'Nederlands', translations: nl },
  fr: { flag: '🇫🇷', name: 'Français', translations: fr }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('tr');
  const [translations, setTranslations] = useState(tr);

  /**
   * Admin panelden düzenlenen metinleri çekip locale dosyasındaki varsayılanların
   * üzerine bindirir. Backend erişilemezse varsayılanlar olduğu gibi kalır —
   * site metinsiz kalmaz.
   */
  const applyOverrides = useCallback(async (langCode) => {
    const defaults = languages[langCode]?.translations || tr;
    try {
      const response = await translationAPI.getTranslationsByLang(langCode);
      const overrides = response.data || {};
      setTranslations(Object.keys(overrides).length > 0 ? { ...defaults, ...overrides } : defaults);
    } catch (err) {
      console.error('Metin düzenlemeleri yüklenirken hata oluştu:', err);
      setTranslations(defaults);
    }
  }, []);

  // Detect browser language and set initial language
  useEffect(() => {
    const detectLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0].toLowerCase();
      
      // Check if browser language is supported
      if (languages[langCode]) {
        return langCode;
      }
      
      // Fallback to supported languages based on region
      if (browserLang.includes('TR')) return 'tr';
      if (browserLang.includes('NL')) return 'nl';
      if (browserLang.includes('FR')) return 'fr';
      if (browserLang.includes('EN')) return 'en';
      
      // Default fallback
      return 'tr';
    };

    const savedLanguage = localStorage.getItem('selectedLanguage');
    const detectedLanguage = savedLanguage || detectLanguage();
    
    setCurrentLanguage(detectedLanguage);
    setTranslations(languages[detectedLanguage].translations);
    applyOverrides(detectedLanguage);
  }, [applyOverrides]);

  const changeLanguage = (langCode) => {
    if (languages[langCode]) {
      setCurrentLanguage(langCode);
      setTranslations(languages[langCode].translations);
      applyOverrides(langCode);
      localStorage.setItem('selectedLanguage', langCode);
      
      // Update document language attribute
      document.documentElement.lang = langCode;
    }
  };

  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return fallback || key;
      }
    }
    
    return value || fallback || key;
  };

  const getCurrentLanguageInfo = () => {
    return languages[currentLanguage];
  };

  const getAvailableLanguages = () => {
    return Object.entries(languages).map(([code, info]) => ({
      code,
      ...info
    }));
  };

  const value = {
    currentLanguage,
    translations,
    changeLanguage,
    // Admin panelde kayıt sonrası metinleri tazelemek için
    refreshTranslations: () => applyOverrides(currentLanguage),
    t,
    getCurrentLanguageInfo,
    getAvailableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
